import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

// workspace.collections(id, org_id, name, icon, position, created_by, created_at) 훅.
// 사용자가 직접 만드는 커스텀 섹션(컬렉션) 목록 조회 + CRUD.
// 테이블이 아직 없을 때(마이그레이션 전)도 깨지지 않도록 try/catch 로 방어 — 빈 목록·no-op.
export default function useCollections() {
  const { user, membership } = useAuth();
  const orgId = membership?.org_id ?? null;
  const userId = user?.id ?? null;

  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCollections = useCallback(async () => {
    if (!orgId) {
      setCollections([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .schema('workspace')
        .from('collections')
        .select('*')
        .eq('org_id', orgId)
        .order('position', { ascending: true });
      if (error) {
        // 테이블 미존재(42P01) 등 — 조용히 빈 목록 처리.
        setCollections([]);
      } else {
        setCollections(data || []);
      }
    } catch {
      setCollections([]);
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  // 다른 곳에서 컬렉션을 바꾸면 갱신
  useEffect(() => {
    const h = () => fetchCollections();
    window.addEventListener('rt:collections', h);
    return () => window.removeEventListener('rt:collections', h);
  }, [fetchCollections]);

  // 컬렉션 생성. 성공 시 생성된 row, 실패 시 null.
  const createCollection = useCallback(
    async ({ name, icon } = {}) => {
      const trimmed = (name || '').trim();
      if (!orgId || !userId || !trimmed) return null;
      try {
        // position: 끝에 추가되도록 timestamp 사용.
        const { data, error } = await supabase
          .schema('workspace')
          .from('collections')
          .insert({
            org_id: orgId,
            name: trimmed,
            icon: icon || '📁',
            position: Date.now(),
            created_by: userId,
          })
          .select('*')
          .single();
        if (error) return null;
        await fetchCollections();
        return data;
      } catch {
        return null;
      }
    },
    [orgId, userId, fetchCollections]
  );

  // 컬렉션 이름/아이콘 수정. 성공 true / 실패 false.
  const renameCollection = useCallback(
    async (id, { name, icon } = {}) => {
      if (!id) return false;
      const patch = {};
      if (name !== undefined) {
        const trimmed = (name || '').trim();
        if (!trimmed) return false;
        patch.name = trimmed;
      }
      if (icon !== undefined) patch.icon = icon;
      if (Object.keys(patch).length === 0) return false;
      try {
        const { error } = await supabase
          .schema('workspace')
          .from('collections')
          .update(patch)
          .eq('id', id);
        if (error) return false;
        await fetchCollections();
        return true;
      } catch {
        return false;
      }
    },
    [fetchCollections]
  );

  // 컬렉션 삭제. 소속 페이지의 collection_id 는 DB on delete set null 로 자동 해제됨.
  const deleteCollection = useCallback(
    async (id) => {
      if (!id) return false;
      try {
        const { error } = await supabase
          .schema('workspace')
          .from('collections')
          .delete()
          .eq('id', id);
        if (error) return false;
        await fetchCollections();
        // 소속 페이지가 다시 개인/공유 섹션으로 돌아가도록 페이지 목록 갱신 신호.
        window.dispatchEvent(new Event('rt:pages'));
        return true;
      } catch {
        return false;
      }
    },
    [fetchCollections]
  );

  return {
    collections,
    loading,
    fetchCollections,
    createCollection,
    renameCollection,
    deleteCollection,
  };
}
